import React, { useEffect, useState } from 'react';
import { Button, Typography, Stack, Grid, Box, Fade } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { getOnboardingUserIdFromLocalStorage } from '../../../helper';
import {
  getOnboardingState,
  saveOnboarding,
} from '../../../store/V2/OnboardingSlice';
import { backButtonProps } from './Step1';
import { actionButtonSelectStyles, actionButtonStyles } from './Step2';
import CustomStepper from './components/CustomStepper';

type Step3Props = {
  onNext: () => void;
  onBack: () => void;
  currentStep: number;
};

const Step3: React.FC<Step3Props> = ({ onNext, onBack, currentStep }) => {
  const dispatch = useDispatch();

  // Store
  const { onboardingData, status: onboardingStatus } = useAppSelector(
    getOnboardingState
  );
  const onboardingUserId = getOnboardingUserIdFromLocalStorage();

  // State
  const [selectedLevel, setSelectedLevel] = useState<string | null>(
    onboardingData?.level || null
  );

  const buttonOptions = [
    'I’m a beginner',
    'I understand the basics, but need support',
    'I’m good, but need help with difficult topics',
    'I’m aiming for top scores / revision only',
  ];

  const isLoading = onboardingStatus === 'loading';

  useEffect(() => {
    if (onboardingData?.level) {
      setSelectedLevel(onboardingData.level);
    }
  }, [onboardingData?.level]);

  const handleContinue = async (level: string) => {
    setSelectedLevel(level);
    await dispatch(
      saveOnboarding({
        _id: onboardingUserId,
        level,
      })
    );

    onNext();
  };

  return (
    <Grid container sx={{ height: '100vh' }}>
      {/* Left section */}
      <Grid
        item
        xs={12}
        sm={4}
        md={6}
        sx={{
          backgroundColor: '#F3F1FE',
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Button {...backButtonProps} onClick={onBack}>
          Back
        </Button>
        <Fade in>
          <img
            src={`${process.env.PUBLIC_URL}/images/onboarding/img-step3.png`}
            alt="step3"
          />
        </Fade>
      </Grid>

      {/* Right section */}
      <Grid
        item
        xs={12}
        sm={8}
        md={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Mobile Back button */}
        <Box display={{ xs: 'block', sm: 'none' }}>
          <Button
            {...backButtonProps}
            sx={{ ...backButtonProps.sx, left: '20px', top: '20px' }}
            onClick={onBack}
          />
        </Box>

        {/* Progress bar */}
        <CustomStepper activeStep={currentStep} />

        <Fade in>
          <Stack
            spacing={8}
            sx={{
              maxWidth: 375,
              width: '100%',
            }}
          >
            <Typography
              fontSize="25px"
              fontWeight="700"
              textAlign="center"
              color={TP_COLORS.gray.darken}
            >
              What’s your command on {onboardingData?.subject || 'subject'}?
            </Typography>

            <Stack spacing={2}>
              {buttonOptions.map((option) => (
                <LoadingButton
                  key={option}
                  sx={
                    !isLoading && selectedLevel === option
                      ? actionButtonSelectStyles
                      : actionButtonStyles
                  }
                  onClick={() => handleContinue(option)}
                  loading={isLoading && selectedLevel === option}
                >
                  {option}
                </LoadingButton>
              ))}
            </Stack>
          </Stack>
        </Fade>
      </Grid>
    </Grid>
  );
};

export default Step3;
